import { useState, useEffect, useCallback, useRef } from 'react';
import { safeGetJSON, safeSetJSON } from '@/lib/safeStorage';
import { toast } from '@/hooks/use-toast';

interface AdvancedEasterEggState {
  discovered: string[];
  cornerSequenceDiscovered: boolean; // Click all 4 corners
  infinityPatternDiscovered: boolean; // Draw infinity pattern
  hiddenTextRevealed: string[];
  reverseScrollDiscovered: boolean; // Scroll up very fast after scrolling down
  midnightVisitor: boolean; // Visit at exactly midnight
  morseCodeDiscovered: boolean; // Click pattern matching SOS
  shakingDiscovered: boolean; // Shake device (mobile)
}

const getDefaultState = (): AdvancedEasterEggState => ({
  discovered: [],
  cornerSequenceDiscovered: false,
  infinityPatternDiscovered: false,
  hiddenTextRevealed: [],
  reverseScrollDiscovered: false,
  midnightVisitor: false,
  morseCodeDiscovered: false,
  shakingDiscovered: false,
});

const STORAGE_KEY = 'advanced_easter_eggs';

export const useAdvancedEasterEggs = () => {
  const [state, setState] = useState<AdvancedEasterEggState>(() =>
    safeGetJSON(STORAGE_KEY, getDefaultState())
  );

  const cornerClicks = useRef<string[]>([]);
  const cornerTimer = useRef<NodeJS.Timeout | null>(null);
  const clickTimestamps = useRef<number[]>([]);
  const lastScrollY = useRef(0);
  const scrollVelocity = useRef(0);

  // Persist state
  useEffect(() => {
    safeSetJSON(STORAGE_KEY, state);
  }, [state]);

  const discover = useCallback((id: string, message: string) => {
    setState(prev => {
      if (prev.discovered.includes(id)) return prev;
      toast({
        title: 'Discovery',
        description: message,
      });
      return {
        ...prev,
        discovered: [...prev.discovered, id],
      };
    });
  }, []);

  // Corner sequence detection: click all 4 corners in order
  const handleCornerClick = useCallback((corner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right') => {
    if (state.cornerSequenceDiscovered) return;

    cornerClicks.current.push(corner);
    
    if (cornerTimer.current) clearTimeout(cornerTimer.current);
    
    cornerTimer.current = setTimeout(() => {
      const sequence = cornerClicks.current.join(',');
      if (sequence === 'top-left,top-right,bottom-right,bottom-left') {
        setState(prev => ({ ...prev, cornerSequenceDiscovered: true }));
        discover('corner_sequence', 'You traced the boundaries.');
      }
      cornerClicks.current = [];
    }, 3000);
  }, [discover, state.cornerSequenceDiscovered]);

  // Morse code detection (SOS: ... --- ...)\\n  // Short click < 200ms, long click > 500ms
  const handleMorseClick = useCallback(() => {
    if (state.morseCodeDiscovered) return;

    const now = Date.now();
    clickTimestamps.current.push(now);
    
    // Keep only last 9 clicks
    if (clickTimestamps.current.length > 9) {
      clickTimestamps.current.shift();
    }

    // Check for SOS pattern
    if (clickTimestamps.current.length >= 9) {
      const gaps = [];
      for (let i = 1; i < clickTimestamps.current.length; i++) {
        gaps.push(clickTimestamps.current[i] - clickTimestamps.current[i - 1]);
      }
      
      // SOS: 3 short, 3 long, 3 short
      // Short gaps < 300ms, long gaps 300-800ms
      const isShort = (gap: number) => gap < 300;
      const isLong = (gap: number) => gap >= 300 && gap < 800;
      
      const pattern = gaps.map((g, i) => {
        if (i < 2) return isShort(g) ? 'S' : null;
        if (i >= 2 && i < 5) return isLong(g) ? 'L' : null;
        return isShort(g) ? 'S' : null;
      });
      
      if (pattern.every((p, i) => {
        if (i < 2) return p === 'S';
        if (i >= 2 && i < 5) return p === 'L';
        return p === 'S';
      })) {
        setState(prev => ({ ...prev, morseCodeDiscovered: true }));
        discover('morse_sos', 'S.O.S. received.');
      }
    }
  }, [discover, state.morseCodeDiscovered]);

  // Reverse scroll detection
  useEffect(() => {
    let lastTime = Date.now();
    
    const handleScroll = () => {
      if (state.reverseScrollDiscovered) return;
      
      const now = Date.now();
      const deltaY = window.scrollY - lastScrollY.current;
      const deltaTime = now - lastTime;
      
      scrollVelocity.current = deltaY / deltaTime;
      
      // Fast reverse scroll (upward) after being at bottom
      if (scrollVelocity.current < -3 && lastScrollY.current > window.innerHeight * 2) {
        setState(prev => ({ ...prev, reverseScrollDiscovered: true }));
        discover('reverse_scroll', 'Going back... but faster.');
      }
      
      lastScrollY.current = window.scrollY;
      lastTime = now;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [discover, state.reverseScrollDiscovered]);

  // Midnight visitor detection
  useEffect(() => {
    if (state.midnightVisitor) return;

    const checkMidnight = () => {
      const now = new Date();
      if (now.getHours() === 0 && now.getMinutes() === 0) {
        setState(prev => ({ ...prev, midnightVisitor: true }));
        discover('midnight_visitor', 'The witching hour welcomes you.');
      }
    };

    checkMidnight();
    const interval = setInterval(checkMidnight, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [discover, state.midnightVisitor]);

  // Device shake detection (mobile)
  useEffect(() => {
    if (state.shakingDiscovered) return;
    if (typeof DeviceMotionEvent === 'undefined') return;

    let lastShake = 0;
    let shakeCount = 0;

    const handleMotion = (e: DeviceMotionEvent) => {
      const acceleration = e.accelerationIncludingGravity;
      if (!acceleration) return;

      const { x, y, z } = acceleration;
      const total = Math.sqrt((x || 0) ** 2 + (y || 0) ** 2 + (z || 0) ** 2);

      if (total > 25) {
        const now = Date.now();
        if (now - lastShake < 500) {
          shakeCount++;
          if (shakeCount >= 5) {
            setState(prev => ({ ...prev, shakingDiscovered: true }));
            discover('shake', 'You shook something loose.');
            shakeCount = 0;
          }
        } else {
          shakeCount = 1;
        }
        lastShake = now;
      }
    };

    window.addEventListener('devicemotion', handleMotion);
    return () => window.removeEventListener('devicemotion', handleMotion);
  }, [discover, state.shakingDiscovered]);

  // Reveal hidden text in a specific room
  const revealHiddenText = useCallback((textId: string, message: string) => {
    setState(prev => {
      if (prev.hiddenTextRevealed.includes(textId)) return prev;
      toast({
        title: 'Hidden Text',
        description: message,
      });
      return {
        ...prev,
        hiddenTextRevealed: [...prev.hiddenTextRevealed, textId],
      };
    });
  }, []);

  const isTextRevealed = useCallback((textId: string) => {
    return state.hiddenTextRevealed.includes(textId);
  }, [state.hiddenTextRevealed]);

  return {
    state,
    discover,
    handleCornerClick,
    handleMorseClick,
    revealHiddenText,
    isTextRevealed,
    totalDiscovered: state.discovered.length,
  };
};
